import { supabase } from '../supabase/client';

const MAX_IMAGE_SIZE = 500 * 1024;

interface PaperMeta {
  id: string;
  title: string;
  description?: string;
  year: string;
  month: string;
  organization: string;
  source: string;
  nature: string;
  total_score: number;
  question_count: number;
  category_id: string | null;
  category_type: string | null;
  level_text: string | null;
  paper_type: string;
  is_published: boolean;
  is_free: boolean;
  remark: string;
  duration: number;
  status: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  edit_history: any[];
  single_choice_count: number;
  multi_choice_count: number;
  judge_count: number;
  coding_count: number;
  imageCount: number;
}

export const paperStorage = {
  async savePaper(paperData: any): Promise<{ success: boolean; message?: string }> {
    try {
      const { questions, images } = this.extractImages(paperData.questions);

      const paperRecord = {
        id: paperData.id,
        title: paperData.title,
        description: paperData.description,
        year: parseInt(paperData.year) || new Date().getFullYear(),
        month: parseInt(paperData.month) || new Date().getMonth() + 1,
        organization: paperData.organization,
        source: paperData.source,
        nature: paperData.nature,
        total_score: paperData.total_score || 0,
        question_count: paperData.question_count || 0,
        questions: questions,
        category_id: paperData.category_id,
        category_type: paperData.category_type,
        level_text: paperData.level_text,
        paper_type: paperData.paper_type || 'real',
        is_published: paperData.is_published || false,
        is_free: paperData.is_free || false,
        remark: paperData.remark,
        duration: paperData.duration || 60,
        status: paperData.status || 'draft',
        single_choice_count: paperData.single_choice_count || 0,
        multi_choice_count: paperData.multi_choice_count || 0,
        judge_count: paperData.judge_count || 0,
        coding_count: paperData.coding_count || 0,
        updated_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('papers')
        .upsert(paperRecord);

      if (error) throw error;

      return { success: true };
    } catch (e: any) {
      return { success: false, message: '保存失败: ' + e.message };
    }
  },

  async getPaperList(): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('papers')
        .select('id, title, updated_at, question_count, total_score, year, month, organization, category_type, level_text, paper_type, is_published, is_free, created_by')
        .order('updated_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch {
      return [];
    }
  },

  async getPaper(paperId: string): Promise<any | null> {
    try {
      
      const { data, error } = await supabase
        .from('papers')
        .select('*')
        .filter('id', 'eq', paperId)
        .maybeSingle();

      if (error) {
        
        return null;
      }
      if (!data) {
        
        return null;
      }

      
      return {
        ...data,
        questions: data.questions || []
      };
    } catch (e) {
      
      return null;
    }
  },

  async deletePaper(paperId: string): Promise<void> {
    try {
      await supabase
        .from('papers')
        .delete()
        .eq('id', paperId);
    } catch (error) {
      
    }
  },

  extractImages(questions: any[]): { questions: any[]; images: string[] } {
    const images: string[] = [];
    const processedQuestions = questions.map(q => {
      const processed = { ...q };

      const extractFromField = (field: string) => {
        if (processed[field] && Array.isArray(processed[field])) {
          processed[field].forEach((img: string) => {
            if (img && img.startsWith('data:')) {
              images.push(img);
            }
          });
          processed[field] = processed[field].map(() => ({ __ref: true }));
        }
      };

      extractFromField('titleImages');
      extractFromField('answerImages');
      extractFromField('scoringPointsImages');
      extractFromField('referenceAnswerImages');

      if (processed.options) {
        processed.options = processed.options.map((opt: any) => {
          if (opt.image && opt.image.startsWith('data:')) {
            images.push(opt.image);
            return { ...opt, image: { __ref: true } };
          }
          return opt;
        });
      }

      return processed;
    });

    return { questions: processedQuestions, images };
  },

  async saveDraft(paperData: any, userId: string): Promise<{ success: boolean; message?: string }> {
    try {
      const draftRecord = {
        user_id: userId,
        paper_id: paperData.id || `draft_${Date.now()}`,
        title: paperData.title || '未命名草稿',
        description: paperData.description,
        year: paperData.year?.toString(),
        month: paperData.month?.toString(),
        organization: paperData.organization,
        source: paperData.source,
        nature: paperData.nature,
        total_score: paperData.total_score || 0,
        question_count: paperData.question_count || 0,
        category_id: paperData.category_id,
        category_type: paperData.category_type,
        level_text: paperData.level_text,
        paper_type: paperData.paper_type || 'scratch',
        is_published: false,
        is_free: paperData.is_free || false,
        remark: paperData.remark,
        duration: paperData.duration || 60,
        status: 'draft',
        questions: paperData.questions || [],
        single_choice_count: paperData.single_choice_count || 0,
        multi_choice_count: paperData.multi_choice_count || 0,
        judge_count: paperData.judge_count || 0,
        coding_count: paperData.coding_count || 0,
        edit_history: paperData.edit_history || []
      };

      const { error } = await supabase
        .from('paper_drafts')
        .upsert(draftRecord);

      if (error) throw error;

      return { success: true };
    } catch (e: any) {
      return { success: false, message: '保存草稿失败: ' + e.message };
    }
  },

  async getDrafts(userId: string): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('paper_drafts')
        .select('*')
        .eq('user_id', userId)
        .order('updated_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch {
      return [];
    }
  },

  async getDraft(draftId: string): Promise<any | null> {
    try {
      const { data, error } = await supabase
        .from('paper_drafts')
        .select('*')
        .eq('paper_id', draftId)
        .maybeSingle();

      if (error) throw error;
      return data;
    } catch {
      return null;
    }
  },

  async deleteDraft(draftId: string): Promise<void> {
    try {
      await supabase
        .from('paper_drafts')
        .delete()
        .eq('paper_id', draftId);
    } catch (error) {
      
    }
  }
};

export default paperStorage;
